import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import IconPicker from '../../SprintDetail/shared/IconPicker';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';

// Styles
import './styles/simple-modal.css';

/**
 * ViewQueryBuilderModal Component
 * 
 * Simplified modal for creating or editing a multi view.
 * Just name and icon - like the create campaign modal.
 */
const ViewQueryBuilderModal = ({ 
  isOpen, 
  onClose, 
  onSave,
  editingView = null, // If provided, we're editing; otherwise creating
}) => {
  // Form state
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('👁️');
  const [error, setError] = useState(null);
  const nameInputRef = useRef(null);
  
  // Icon picker state
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerPosition, setIconPickerPosition] = useState(null);
  const iconRef = useRef(null);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingView) {
        // Editing existing view
        setName(editingView.name);
        setIcon(editingView.icon || '👁️');
      } else {
        // New view
        setName('');
        setIcon('👁️');
      }
      setError(null);
      setShowIconPicker(false);
      
      // Focus name input
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen, editingView]);

  // Icon picker handlers
  const handleIconClick = (e) => {
    e.stopPropagation();
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuHeight = 440;
      const menuWidth = 380;
      
      let top = rect.bottom + 4;
      if (top + menuHeight > viewportHeight) {
        top = rect.top - menuHeight - 4;
      }
      let left = rect.left;
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 10;
      }
      
      setIconPickerPosition({ x: left, y: top });
    }
    setShowIconPicker(true);
  };

  const handleIconChange = (newIcon) => {
    setIcon(newIcon || '👁️');
  };

  const handleIconDeleted = () => {
    setIcon('👁️');
    setShowIconPicker(false);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter a view name');
      return;
    }

    const view = {
      id: editingView?.id || `view-${Date.now()}`,
      name: name.trim(),
      icon,
      viewMode: editingView?.viewMode || 'flow',
      query: editingView?.query || {
        scope: [], // Start with no campaigns - user adds them via drag or picker
        explode: { enabled: false, depth: 1 },
        filters: {},
        pinnedTasks: [],
      },
      isBuiltIn: editingView?.isBuiltIn || false,
      createdAt: editingView?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onSave?.(view);
    onClose();
  };

  const handleClose = () => {
    // Don't close modal if icon picker is open
    if (showIconPicker) {
      setShowIconPicker(false);
      return;
    }
    setError(null);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (showIconPicker) {
      setShowIconPicker(false);
      return;
    }
    handleClose();
  };

  if (!isOpen) return null;

  const isEditing = !!editingView;
  const title = isEditing ? 'Edit Multi View' : 'Create Multi View';

  const modalContent = (
    <>
      <div className="view-simple-modal__overlay" onClick={handleOverlayClick}>
        <div className="view-simple-modal" onClick={(e) => e.stopPropagation()}>
          <div className="view-simple-modal__header">
            <h2 className="view-simple-modal__title">{title}</h2>
            <button className="view-simple-modal__close" onClick={handleClose}>×</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="view-simple-modal__body">
              {error && (
                <div className="view-simple-modal__error">{error}</div>
              )}

              <div className="view-simple-modal__field">
                <label className="view-simple-modal__label">Enter Multi View Name:</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  className="view-simple-modal__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., All Active Projects"
                />
              </div>

              <div className="view-simple-modal__field">
                <label className="view-simple-modal__label">
                  Choose an Icon <span className="view-simple-modal__optional">(optional)</span>:
                </label>
                <button
                  ref={iconRef}
                  type="button"
                  className={`view-simple-modal__icon-btn ${icon !== '👁️' ? 'view-simple-modal__icon-btn--has-icon' : ''}`}
                  onClick={handleIconClick}
                  title="Choose Icon"
                >
                  {renderIcon(icon, { size: '24px', fallback: '👁️' })}
                  <span className="view-simple-modal__icon-label">
                    {icon !== '👁️' ? 'Change' : 'Add Icon'}
                  </span>
                </button>
              </div>
            </div>

            <div className="view-simple-modal__footer">
              <button 
                type="button" 
                className="view-simple-modal__btn view-simple-modal__btn--secondary"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="view-simple-modal__btn view-simple-modal__btn--primary"
                disabled={!name.trim()}
              >
                {isEditing ? 'Save Changes' : 'Create View'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Icon Picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={icon}
          onIconChange={handleIconChange}
          onIconDeleted={handleIconDeleted}
          onClose={() => setShowIconPicker(false)}
          position={iconPickerPosition}
        />
      )}
    </>
  );

  return createPortal(modalContent, document.body);
};

export default ViewQueryBuilderModal;
